import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Copy } from "lucide-react";
import { adminApi, errMsg } from "../../lib/api";
import { CouponReport } from "./CouponReport";

const input = "border-2 border-slate-200 px-3 py-2 text-sm font-semibold outline-none focus:border-slate-900";

const GivenTo = ({ code, value }) => {
  const [v, setV] = useState(value);
  const [saved, setSaved] = useState(value);
  const save = async () => {
    if (v.trim() === saved) return;
    try { await adminApi().put(`/admin/coupons/${code}/note`, { given_to: v }); setSaved(v.trim()); toast.success(`Saved for ${code}`); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return <input value={v} maxLength={120} onChange={(e) => setV(e.target.value)} onBlur={save} onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
    placeholder="e.g. Mary – St Brigid's U10" aria-label={`Who ${code} was given to`} data-testid={`coupon-given-to-${code}`}
    className="w-full min-w-[160px] border border-slate-200 px-2 py-1 text-xs outline-none focus:border-slate-900" />;
};

export const Coupons = () => {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ percent: 10, count: 5, expires_on: "" });
  const [fresh, setFresh] = useState([]);
  const load = () => adminApi().get("/admin/coupons").then((r) => setItems(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);
  const create = (e) => {
    e.preventDefault();
    adminApi().post("/admin/coupons", { ...form, count: Number(form.count), expires_on: form.expires_on || null })
      .then((r) => { setFresh(r.data.map((c) => c.code)); toast.success(`${r.data.length} codes created`); load(); }).catch((err) => toast.error(errMsg(err)));
  };
  const copy = (t) => navigator.clipboard.writeText(t).then(() => toast.success("Copied"));
  const used = items.filter((c) => c.used).length;
  return (
    <section className="mt-10" data-testid="admin-coupons">
      <div className="eyebrow">Promotions</div>
      <h2 className="font-display text-2xl font-black uppercase">Discount codes</h2>
      <p className="mt-1 text-sm text-slate-600">One-time codes, taken off shields only (not delivery). A code is marked used once its order is paid. Club fundraiser shares aren't affected.</p>
      <form onSubmit={create} className="mt-4 flex flex-wrap items-end gap-3">
        <label className="text-xs font-bold">Discount<select value={form.percent} onChange={(e) => setForm({ ...form, percent: Number(e.target.value) })} className={`${input} mt-1 block`} data-testid="coupon-percent-select">
          {[10, 20, 50].map((p) => <option key={p} value={p}>{p}% off</option>)}</select></label>
        <label className="text-xs font-bold">How many<input type="number" min={1} max={200} value={form.count} onChange={(e) => setForm({ ...form, count: e.target.value })} className={`${input} mt-1 block w-24`} data-testid="coupon-count-input" /></label>
        <label className="text-xs font-bold">Expires (optional)<input type="date" value={form.expires_on} onChange={(e) => setForm({ ...form, expires_on: e.target.value })} className={`${input} mt-1 block`} data-testid="coupon-expiry-input" /></label>
        <button type="submit" className="btn-blaze px-4 py-2.5 text-sm" data-testid="coupon-create-button">Create codes</button>
      </form>
      {fresh.length > 0 && (
        <div className="mt-4 border-2 border-lime-500 bg-lime-50 p-3" data-testid="coupon-fresh">
          <div className="flex items-center justify-between text-sm font-bold">New codes<button type="button" onClick={() => copy(fresh.join("\n"))} className="flex items-center gap-1 text-xs underline" data-testid="coupon-copy-all"><Copy className="h-3.5 w-3.5" /> Copy all</button></div>
          <div className="mt-2 flex flex-wrap gap-2 font-mono text-sm">{fresh.map((c) => <span key={c} className="bg-white px-2 py-0.5">{c}</span>)}</div>
        </div>
      )}
      <p className="mt-4 text-xs font-bold text-slate-500" data-testid="coupon-stats">{items.length} codes · {used} used · {items.length - used} unused</p>
      <div className="mt-2 max-h-72 overflow-auto border-2 border-slate-200 bg-white">
        <table className="w-full min-w-[560px] text-left text-xs" data-testid="coupon-table">
          <thead className="sticky top-0 bg-slate-100 uppercase"><tr><th className="px-3 py-2">Code</th><th className="px-3 py-2">Off</th><th className="px-3 py-2">Expires</th><th className="px-3 py-2">Status</th><th className="px-3 py-2">Given to</th></tr></thead>
          <tbody>{items.map((c) => (
            <tr key={c.code} className="border-t border-slate-100" data-testid={`coupon-row-${c.code}`}>
              <td className="px-3 py-1.5 font-mono font-bold"><button type="button" onClick={() => copy(c.code)} className="hover:underline">{c.code}</button></td>
              <td className="px-3 py-1.5">{c.percent}%</td><td className="px-3 py-1.5">{c.expires_on || "—"}</td>
              <td className="px-3 py-1.5">{c.used ? <span className="font-bold text-red-600">Used · {c.used_order}</span> : c.expires_on && c.expires_on < new Date().toISOString().slice(0, 10) ? <span className="font-bold text-slate-400">Expired</span> : <span className="font-bold text-lime-700">Unused</span>}</td>
              <td className="px-2 py-1"><GivenTo code={c.code} value={c.given_to || ""} /></td>
            </tr>
          ))}</tbody>
        </table>
      </div>
      <CouponReport refreshKey={items.length} />
    </section>
  );
};
